/**
 * Confirmation dialog shown when a provider tries to sign a note that still
 * has unfilled bracketed placeholders, while the org's
 * "block signing until placeholders filled" policy is enabled.
 *
 * This is a SOFT gate: the provider can still sign ("Sign anyway"), but is
 * nudged to review the outstanding placeholders first.
 */
import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

interface SignWithPlaceholdersDialogProps {
  open: boolean;
  /** Number of outstanding placeholders. */
  count: number;
  /** Jump to the next placeholder (also closes the dialog). */
  onReview: () => void;
  /** Proceed with signing despite the outstanding placeholders. */
  onSignAnyway: () => void;
  /** Dismiss without signing. */
  onClose: () => void;
}

export const SignWithPlaceholdersDialog: React.FC<
  SignWithPlaceholdersDialogProps
> = ({ open, count, onReview, onSignAnyway, onClose }) => {
  const plural = count === 1 ? '' : 's';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="sign-with-placeholders-title"
      maxWidth="xs"
    >
      <DialogTitle id="sign-with-placeholders-title">
        Unfilled placeholders
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          This note still has {count} unfilled placeholder{plural} (text in
          [brackets]). Review {count === 1 ? 'it' : 'them'}, or sign anyway if{' '}
          {count === 1 ? 'it is' : 'they are'} intentional.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onReview}>Review placeholder{plural}</Button>
        <Button onClick={onSignAnyway} variant="contained" color="primary">
          Sign anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
};
